import * as tus from 'tus-js-client';

// The browser uploads bytes straight to Bunny using a signature the backend
// produced. The API key is never here -- Vue CLI inlines every VUE_APP_* into
// dist/js/app.*.js, so a key in this bundle would be readable by any admin.

// 6 MB. Larger than the 3 MB the old vue-dropzone used, because TUS resumes
// from the server's offset rather than re-sending everything after a drop.
const CHUNK_SIZE = 6 * 1024 * 1024;

// Bunny's upload signature is deliberately long-lived (24h, so an overnight
// upload on slow campus internet can finish), but a signature that is simply
// wrong -- revoked, clock-skewed, buggy -- would otherwise turn a
// refresh-then-401 cycle into an infinite loop of requests against Bunny.
// This caps how many times we'll try to recover from an auth rejection
// before giving up and surfacing the error.
const MAX_SIGNATURE_REFRESH_ATTEMPTS = 3;

/**
 * @param {object} options
 * @param {File} options.file
 * @param {object} options.ticket from the createBunnyUpload mutation
 * @param {string} options.title the same string that was sent to createBunnyUpload. Required in
 *   practice -- see the metadata comment below for why passing the file's own name here is the one
 *   thing this function must not do.
 * @param {function(number)} options.onProgress receives 0-100
 * @param {function()} options.onSuccess
 * @param {function(Error)} options.onError called for any failure that isn't recovered by
 *   `refreshTicket` (or when `refreshTicket` isn't supplied, or the recovery attempts are
 *   exhausted)
 * @param {function(): Promise<object>} [options.refreshTicket] called when Bunny rejects the
 *   upload's signature (HTTP 401/403). Must resolve a fresh ticket for the *same* video guid
 *   (`authorizationSignature` / `authorizationExpire`); the upload then resumes from its current
 *   offset instead of restarting. Optional -- omit to leave an expired signature as a plain
 *   `onError`.
 * @return {{abort: function()}} `abort()` stops the upload. Safe to call at any point, including
 *   during the async gap before the underlying tus.Upload has actually started -- it will not be
 *   silently overridden by that start.
 */
export function startBunnyUpload({
  file, ticket, title, onProgress, onSuccess, onError, refreshTicket,
}) {
  let aborted = false;
  let refreshAttempts = 0;

  const upload = new tus.Upload(file, {
    endpoint: ticket.tusEndpoint,
    retryDelays: [0, 3000, 5000, 10000, 20000],
    chunkSize: CHUNK_SIZE,
    headers: {
      AuthorizationSignature: ticket.authorizationSignature,
      AuthorizationExpire: String(ticket.authorizationExpire),
      LibraryId: String(ticket.libraryId),
      VideoId: ticket.videoGuid,
    },
    // Bunny lists `title` as a REQUIRED TUS metadata parameter, and whatever is
    // sent here is the title the video ends up with -- it lands after, and wins
    // over, the title createBunnyUpload already set on the video object via
    // POST /library/{id}/videos. This used to send `file.name`, which silently
    // undid buildBunnyTitle for every upload: verified on 2026-08-14 against
    // library 722548, where all three videos carried their raw file name
    // ("IT1001_2_1_บทเรียน (23_05_68).mp4") instead of the built title.
    //
    // The `|| file.name` is a last resort for the required field, not a
    // default: tus-js-client would otherwise stringify an absent title into the
    // literal "undefined". The only caller (VdoSetTopicEditBunny.vue) always
    // passes the same string it sent to createBunnyUpload, which is what keeps
    // the two sides agreeing.
    metadata: {
      filetype: file.type,
      title: title || file.name,
    },
    // tus-js-client's default browser fingerprint is
    // [file.name, file.type, file.size, file.lastModified, options.endpoint].join('-'), and
    // `endpoint` is the one shared Bunny TUS endpoint for every upload -- nothing in it says
    // *which* video. Without the video guid mixed in, canceling (which deletes the Bunny-side
    // video) and re-picking the same file resumes PATCHing to the old, now-dead upload URL.
    // Keying on the guid means a resume can only ever match the same video.
    fingerprint: (fingerprintFile, fingerprintOptions) => Promise.resolve(
      ['bunny', ticket.videoGuid, fingerprintFile.name, fingerprintFile.type,
        fingerprintFile.size, fingerprintFile.lastModified, fingerprintOptions.endpoint]
        .join('-'),
    ),
    onProgress(bytesUploaded, bytesTotal) {
      if (!onProgress || !bytesTotal) return;
      onProgress(Math.round((bytesUploaded / bytesTotal) * 100));
    },
    onSuccess() {
      if (onSuccess) onSuccess();
    },
    onError(err) {
      const status = err && err.originalResponse ? err.originalResponse.getStatus() : 0;
      const isAuthRejection = status === 401 || status === 403;

      if (aborted || !isAuthRejection || !refreshTicket
        || refreshAttempts >= MAX_SIGNATURE_REFRESH_ATTEMPTS) {
        if (onError) onError(err);
        return;
      }

      refreshAttempts += 1;
      refreshTicket()
        .then((freshTicket) => {
          if (aborted) return;
          upload.options.headers.AuthorizationSignature = freshTicket.authorizationSignature;
          upload.options.headers.AuthorizationExpire = String(freshTicket.authorizationExpire);
          // Resumes from the offset already reached; does not restart the transfer.
          upload.start();
        })
        .catch(() => {
          if (onError) onError(err);
        });
    },
  });

  // Resume from a previous attempt on this file if one exists.
  upload.findPreviousUploads().then((previous) => {
    // upload.start() unconditionally resets tus-js-client's internal _aborted flag, which would
    // silently undo an abort() called during this async gap. Guard it here instead.
    if (aborted) return;
    if (previous.length) upload.resumeFromPreviousUpload(previous[0]);
    upload.start();
  });

  return {
    abort() {
      aborted = true;
      upload.abort();
    },
  };
}
